import React, { useEffect, useMemo, useState } from "react";
import Draggable from "react-draggable";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { createAutocompleteFilter } from "pages/utils/autocompleteFilter";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";

function PaperComponent(props) {
  return (
    <Draggable
      handle="#dialog-objetivo-v2-patamar-editar-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

const EMPTY_FORM = {
  ordem: 1,
  nome: "",
  reffabricante: "",
  marcas_modo: "QUALQUER",
  valor_empresa: "",
  marcas: [],
};

const MODOS_MARCAS = [
  {
    value: "QUALQUER",
    label: "Qualquer marca",
  },
  {
    value: "TODAS",
    label: "Todas as marcas",
  },
];

const MODOS_SUBMARCAS = [
  {
    value: "",
    label: "Todas as submarcas",
  },
  {
    value: "INCLUIR",
    label: "Incluir apenas",
  },
  {
    value: "EXCLUIR",
    label: "Excluir",
  },
];

const MODOS_EMBALAGENS = [
  {
    value: "",
    label: "Todas as embalagens",
  },
  {
    value: "INCLUIR",
    label: "Incluir apenas",
  },
  {
    value: "EXCLUIR",
    label: "Excluir",
  },
];

const MODOS_MATERIAIS = [
  {
    value: "",
    label: "Todos os materiais",
  },
  {
    value: "INCLUIR",
    label: "Incluir apenas",
  },
  {
    value: "EXCLUIR",
    label: "Excluir",
  },
];

export default function DialogObjetivoV2PatamarEditar({
  open,
  onClose,
  onGuardar,
  patamar = null,
  ordemSugerida = 1,
  reffabricanteSugerido = "",
}) {
  const { showAlert } = useAlert();

  const [loadingFabricantes, setLoadingFabricantes] = useState(false);
  const [fabricantes, setFabricantes] = useState([]);

  const [loadingMarcas, setLoadingMarcas] = useState(false);
  const [marcasOpcoes, setMarcasOpcoes] = useState([]);

  const [loadingSubmarcas, setLoadingSubmarcas] = useState({});
  const [submarcasOpcoes, setSubmarcasOpcoes] = useState({});

  const [loadingEmbalagens, setLoadingEmbalagens] = useState({});
  const [embalagensOpcoes, setEmbalagensOpcoes] = useState({});

  const [loadingMateriais, setLoadingMateriais] = useState({});
  const [materiaisOpcoes, setMateriaisOpcoes] = useState({});
  const [imgsMateriaisByRef, setImgsMateriaisByRef] = useState({});

  const [pesquisaMateriais, setPesquisaMateriais] = useState({});

  const [form, setForm] = useState({
    ...EMPTY_FORM,
  });

  const isEdicao = Boolean(patamar);

  const filtroMarcas = useMemo(
    () => createAutocompleteFilter((option) => `${option?.ref || ""} ${option?.nome || ""}`),
    []
  );

  const filtroSubmarcas = useMemo(
    () => createAutocompleteFilter((option) => `${option?.ref || ""} ${option?.nome || ""}`),
    []
  );

  const filtroEmbalagens = useMemo(
    () => createAutocompleteFilter((option) => `${option?.ref || ""} ${option?.nome || ""}`),
    []
  );

  const filtroMateriais = useMemo(
    () => createAutocompleteFilter((option) => `${option?.ref || ""} ${option?.nome || ""}`),
    []
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    if (patamar) {
      setForm({
        ...EMPTY_FORM,
        ordem: Number(patamar?.ordem || ordemSugerida || 1),
        nome: patamar?.nome || "",
        reffabricante: patamar?.reffabricante || "",
        marcas_modo: patamar?.marcas_modo || "QUALQUER",
        valor_empresa: patamar?.valor_empresa ?? "",
        marcas: Array.isArray(patamar?.marcas) ? patamar.marcas : [],
      });
    } else {
      setForm({
        ...EMPTY_FORM,
        ordem: Number(ordemSugerida || 1),
        reffabricante: String(reffabricanteSugerido || "").trim(),
      });
    }
  }, [open, patamar, ordemSugerida, reffabricanteSugerido]);

  useEffect(() => {
    let ativo = true;

    const carregarFabricantes = async () => {
      if (!open) {
        return;
      }

      setLoadingFabricantes(true);

      try {
        const [payload, err] = await handleApi(
          api.post("Comercial/SelObjetivoV2CatalogosCabecalho")
        );

        if (!ativo) {
          return;
        }

        if (err) {
          showAlert(err, "error");
          return;
        }

        const data = payload?.data ?? payload ?? {};

        setFabricantes(Array.isArray(data?.fabricantes) ? data.fabricantes : []);
      } finally {
        if (ativo) {
          setLoadingFabricantes(false);
        }
      }
    };

    carregarFabricantes();

    return () => {
      ativo = false;
    };
  }, [open, showAlert]);

  useEffect(() => {
    let ativo = true;

    const carregarMarcas = async () => {
      const reffabricante = String(form.reffabricante || "").trim();

      if (!open || !reffabricante) {
        setMarcasOpcoes([]);
        return;
      }

      setLoadingMarcas(true);

      try {
        const [payload, err] = await handleApi(
          api.post("Comercial/SelObjetivoV2MarcasPorFabricante", {
            reffabricante,
            term: "",
          })
        );

        if (!ativo) {
          return;
        }

        if (err) {
          showAlert(err, "error");
          setMarcasOpcoes([]);
          return;
        }

        const rows = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
          ? payload.data
          : [];

        setMarcasOpcoes(rows);
      } finally {
        if (ativo) {
          setLoadingMarcas(false);
        }
      }
    };

    carregarMarcas();

    return () => {
      ativo = false;
    };
  }, [open, form.reffabricante, showAlert]);

  const modoMarcasSelecionado = useMemo(
    () => MODOS_MARCAS.find((x) => x.value === form.marcas_modo) || MODOS_MARCAS[0],
    [form.marcas_modo]
  );

  const carregarSubmarcasMarca = async (marca) => {
    const reffabricante = String(form.reffabricante || "").trim();
    const refmarca = String(marca?.refmarca || marca?.ref || "").trim();

    if (!reffabricante || !refmarca) {
      return;
    }

    if (Array.isArray(submarcasOpcoes[refmarca])) {
      return;
    }

    setLoadingSubmarcas((prev) => ({
      ...prev,
      [refmarca]: true,
    }));

    try {
      const [payload, err] = await handleApi(
        api.post("Comercial/SelObjetivoV2SubmarcasPorMarca", {
          reffabricante,
          refmarca,
          term: "",
        })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];

      setSubmarcasOpcoes((prev) => ({
        ...prev,
        [refmarca]: rows,
      }));
    } finally {
      setLoadingSubmarcas((prev) => ({
        ...prev,
        [refmarca]: false,
      }));
    }
  };

  const carregarEmbalagensMarca = async (marca) => {
    const reffabricante = String(form.reffabricante || "").trim();
    const refmarca = String(marca?.refmarca || marca?.ref || "").trim();

    if (!reffabricante || !refmarca) {
      return;
    }

    if (Array.isArray(embalagensOpcoes[refmarca])) {
      return;
    }

    setLoadingEmbalagens((prev) => ({
      ...prev,
      [refmarca]: true,
    }));

    try {
      const [payload, err] = await handleApi(
        api.post("Comercial/SelObjetivoV2EmbalagensPorMarca", {
          reffabricante,
          refmarca,
          term: "",
        })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];

      setEmbalagensOpcoes((prev) => ({
        ...prev,
        [refmarca]: rows,
      }));
    } finally {
      setLoadingEmbalagens((prev) => ({
        ...prev,
        [refmarca]: false,
      }));
    }
  };

  const carregarImagensMateriais = async (rows) => {
    const refs = Array.from(
      new Set(
        (Array.isArray(rows) ? rows : [])
          .map((item) => String(item?.ref || item?.refmaterial || "").trim())
          .filter(Boolean)
      )
    );

    const faltam = refs.filter((ref) => !imgsMateriaisByRef[ref]);

    if (faltam.length === 0) {
      return;
    }

    const [dados, err] = await handleApi(
      api.post("Materiais/SelMateriaisImagens", {
        refs: faltam,
      })
    );

    if (err) {
      return;
    }

    const mapa = {};

    (Array.isArray(dados) ? dados : []).forEach((item) => {
      const ref = String(item?.refmaterial || "").trim();
      const url = String(item?.url || "").trim();

      if (ref && url) {
        mapa[ref] = url;
      }
    });

    setImgsMateriaisByRef((prev) => ({
      ...prev,
      ...mapa,
    }));
  };

  const carregarMateriaisMarca = async (marca) => {
    const reffabricante = String(form.reffabricante || "").trim();
    const refmarca = String(marca?.refmarca || marca?.ref || "").trim();

    if (!reffabricante || !refmarca) {
      return;
    }

    const submarcasSentido = String(marca?.submarcas_sentido || "")
      .trim()
      .toUpperCase();

    const submarcas = (Array.isArray(marca?.submarcas) ? marca.submarcas : [])
      .map((item) => String(item?.refsubmarca || item?.ref || "").trim())
      .filter(Boolean);

    const embalagensSentido = String(marca?.embalagens_sentido || "")
      .trim()
      .toUpperCase();

    const embalagens = (Array.isArray(marca?.embalagens) ? marca.embalagens : [])
      .map((item) => String(item?.refembalagem || item?.ref || "").trim())
      .filter(Boolean);

    setLoadingMateriais((prev) => ({
      ...prev,
      [refmarca]: true,
    }));

    try {
      const [payload, err] = await handleApi(
        api.post("Comercial/SelObjetivoV2MateriaisPorMarca", {
          reffabricante,
          refmarca,
          submarcas_sentido: submarcasSentido,
          submarcas,
          embalagens_sentido: embalagensSentido,
          embalagens,
          term: "",
        })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];

      setMateriaisOpcoes((prev) => ({
        ...prev,
        [refmarca]: rows,
      }));

      await carregarImagensMateriais(rows);
    } finally {
      setLoadingMateriais((prev) => ({
        ...prev,
        [refmarca]: false,
      }));
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    const marcas = Array.isArray(form.marcas) ? form.marcas : [];

    marcas.forEach((marca) => {
      carregarSubmarcasMarca(marca);
      carregarEmbalagensMarca(marca);
      carregarMateriaisMarca(marca);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.reffabricante, form.marcas]);

  const fabricanteSelecionado = useMemo(
    () => fabricantes.find((x) => String(x.ref) === String(form.reffabricante)) || null,
    [fabricantes, form.reffabricante]
  );

  const marcasSelecionadas = useMemo(() => {
    const refsSelecionadas = new Set(
      (Array.isArray(form.marcas) ? form.marcas : [])
        .map((x) => String(x?.refmarca || x?.ref || "").trim())
        .filter(Boolean)
    );

    return marcasOpcoes.filter((option) => refsSelecionadas.has(String(option?.ref || "").trim()));
  }, [marcasOpcoes, form.marcas]);

  const guardar = () => {
    const nome = String(form.nome || "")
      .trim()
      .toUpperCase();

    if (!nome) {
      return;
    }

    onGuardar?.({
      ...form,
      ordem: Number(form.ordem || 0),
      nome,
      fabricante_nome: fabricanteSelecionado?.nome || fabricanteSelecionado?.ref || "",
      valor_empresa: form.valor_empresa === "" ? null : Number(form.valor_empresa),
      marcas: Array.isArray(form.marcas) ? form.marcas : [],
      filtros: Array.isArray(patamar?.filtros) ? patamar.filtros : [],
      bloqueios: Array.isArray(patamar?.bloqueios) ? patamar.bloqueios : [],
      equipas: Array.isArray(patamar?.equipas) ? patamar.equipas : [],
      vendedores: Array.isArray(patamar?.vendedores) ? patamar.vendedores : [],
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperComponent={PaperComponent}
      PaperProps={{
        sx: {
          width: {
            xs: "calc(100vw - 16px)",
            sm: "92vw",
            md: 860,
          },
          maxWidth: "calc(100vw - 16px)",
        },
      }}
    >
      <DialogTitle
        id="dialog-objetivo-v2-patamar-editar-title"
        sx={{
          cursor: "move",
        }}
      >
        {isEdicao ? "Editar patamar" : "Novo patamar"}
      </DialogTitle>

      <DialogContent dividers>
        <MDBox>
          <MDBox
            display="grid"
            gridTemplateColumns={{
              xs: "1fr",
              sm: "120px minmax(0, 1fr)",
            }}
            gap={2}
          >
            <MDBox>
              <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                Ordem
              </MDTypography>

              <MDInput
                type="number"
                fullWidth
                value={form.ordem}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    ordem: e.target.value,
                  }))
                }
                inputProps={{
                  min: 1,
                  step: 1,
                }}
              />
            </MDBox>

            <MDBox>
              <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                Nome
              </MDTypography>

              <MDInput
                fullWidth
                value={form.nome}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    nome: e.target.value,
                  }))
                }
                onBlur={() =>
                  setForm((prev) => ({
                    ...prev,
                    nome: String(prev.nome || "")
                      .trim()
                      .toUpperCase(),
                  }))
                }
              />
            </MDBox>

            <MDBox
              sx={{
                gridColumn: {
                  xs: "auto",
                  sm: "1 / -1",
                },
              }}
            >
              <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                Fabricante
              </MDTypography>

              <Autocomplete
                size="small"
                options={fabricantes}
                loading={loadingFabricantes}
                getOptionLabel={(option) => option?.nome || option?.ref || ""}
                value={fabricanteSelecionado}
                onChange={(_, value) => {
                  const novaRefFabricante = value?.ref || "";

                  if (String(form.reffabricante || "") !== String(novaRefFabricante)) {
                    setSubmarcasOpcoes({});
                    setLoadingSubmarcas({});
                    setEmbalagensOpcoes({});
                    setLoadingEmbalagens({});
                    setMateriaisOpcoes({});
                    setLoadingMateriais({});
                    setPesquisaMateriais({});
                  }

                  setForm((prev) => ({
                    ...prev,
                    reffabricante: novaRefFabricante,
                    marcas:
                      String(prev.reffabricante || "") === String(novaRefFabricante)
                        ? prev.marcas
                        : [],
                  }));
                }}
                isOptionEqualToValue={(option, value) => String(option?.ref) === String(value?.ref)}
                disableClearable
                renderInput={(params) => <TextField {...params} placeholder="Selecione..." />}
              />
            </MDBox>

            <MDBox
              sx={{
                gridColumn: {
                  xs: "auto",
                  sm: "1 / -1",
                },
              }}
            >
              <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                Marcas
              </MDTypography>

              <Autocomplete
                multiple
                size="small"
                options={marcasOpcoes}
                loading={loadingMarcas}
                disabled={!String(form.reffabricante || "").trim()}
                filterOptions={filtroMarcas}
                getOptionLabel={(option) => option?.nome || option?.ref || ""}
                value={marcasSelecionadas}
                onChange={(_, values) => {
                  setForm((prev) => ({
                    ...prev,
                    marcas: values.map((option) => {
                      const existente = (Array.isArray(prev.marcas) ? prev.marcas : []).find(
                        (x) => String(x?.refmarca || x?.ref || "") === String(option?.ref || "")
                      );

                      return {
                        ...(existente || {}),
                        refmarca: option?.ref || "",
                        marca: option?.nome || option?.ref || "",
                      };
                    }),
                  }));
                }}
                isOptionEqualToValue={(option, value) => String(option?.ref) === String(value?.ref)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={marcasSelecionadas.length === 0 ? "Selecione..." : ""}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingMarcas ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </MDBox>

            {(Array.isArray(form.marcas) ? form.marcas : []).map((marca) => {
              const refmarca = String(marca?.refmarca || marca?.ref || "").trim();
              const nomeMarca = marca?.marca || marca?.nome || refmarca;

              const modoSubmarcas =
                MODOS_SUBMARCAS.find(
                  (x) =>
                    x.value ===
                    String(marca?.submarcas_sentido || "")
                      .trim()
                      .toUpperCase()
                ) || MODOS_SUBMARCAS[0];

              const opcoes = Array.isArray(submarcasOpcoes[refmarca])
                ? submarcasOpcoes[refmarca]
                : [];

              const refsSelecionadas = new Set(
                (Array.isArray(marca?.submarcas) ? marca.submarcas : [])
                  .map((x) => String(x?.refsubmarca || x?.ref || "").trim())
                  .filter(Boolean)
              );

              const selecionadas = opcoes.filter((option) =>
                refsSelecionadas.has(String(option?.ref || "").trim())
              );

              const modoEmbalagens =
                MODOS_EMBALAGENS.find(
                  (x) =>
                    x.value ===
                    String(marca?.embalagens_sentido || "")
                      .trim()
                      .toUpperCase()
                ) || MODOS_EMBALAGENS[0];

              const opcoesEmbalagens = Array.isArray(embalagensOpcoes[refmarca])
                ? embalagensOpcoes[refmarca]
                : [];

              const refsEmbalagensSelecionadas = new Set(
                (Array.isArray(marca?.embalagens) ? marca.embalagens : [])
                  .map((x) => String(x?.refembalagem || x?.ref || "").trim())
                  .filter(Boolean)
              );

              const embalagensSelecionadas = opcoesEmbalagens.filter((option) =>
                refsEmbalagensSelecionadas.has(String(option?.ref || "").trim())
              );

              const modoMateriais =
                MODOS_MATERIAIS.find(
                  (x) =>
                    x.value ===
                    String(marca?.materiais_sentido || "")
                      .trim()
                      .toUpperCase()
                ) || MODOS_MATERIAIS[0];

              const opcoesMateriais = Array.isArray(materiaisOpcoes[refmarca])
                ? materiaisOpcoes[refmarca]
                : [];

              const refsMateriaisSelecionados = new Set(
                (Array.isArray(marca?.materiais) ? marca.materiais : [])
                  .map((x) => String(x?.refmaterial || x?.ref || "").trim())
                  .filter(Boolean)
              );

              const materiaisSelecionados = opcoesMateriais.filter((option) =>
                refsMateriaisSelecionados.has(String(option?.ref || "").trim())
              );

              return (
                <MDBox
                  key={refmarca}
                  sx={{
                    gridColumn: {
                      xs: "auto",
                      sm: "1 / -1",
                    },
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <MDTypography variant="button" fontWeight="bold" display="block" mb={1.5}>
                    {nomeMarca}
                  </MDTypography>

                  <MDBox
                    display="grid"
                    gridTemplateColumns={{
                      xs: "1fr",
                      md: "220px minmax(0, 1fr)",
                    }}
                    gap={2}
                  >
                    <MDBox>
                      <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                        Modo das submarcas
                      </MDTypography>

                      <Autocomplete
                        size="small"
                        options={MODOS_SUBMARCAS}
                        getOptionLabel={(option) => option?.label || ""}
                        value={modoSubmarcas}
                        onChange={(_, value) => {
                          const novoSentido = value?.value || "";

                          setForm((prev) => ({
                            ...prev,
                            marcas: (Array.isArray(prev.marcas) ? prev.marcas : []).map((item) => {
                              const refItem = String(item?.refmarca || item?.ref || "").trim();

                              if (refItem !== refmarca) {
                                return item;
                              }

                              return {
                                ...item,
                                submarcas_sentido: novoSentido,
                                submarcas: novoSentido === "" ? [] : item?.submarcas || [],
                              };
                            }),
                          }));
                        }}
                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                        disableClearable
                        renderInput={(params) => <TextField {...params} />}
                      />
                    </MDBox>

                    <MDBox>
                      <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                        Submarcas
                      </MDTypography>

                      <Autocomplete
                        multiple
                        size="small"
                        disableCloseOnSelect
                        options={opcoes}
                        loading={Boolean(loadingSubmarcas[refmarca])}
                        disabled={modoSubmarcas.value === ""}
                        filterOptions={filtroSubmarcas}
                        getOptionLabel={(option) => option?.nome || option?.ref || ""}
                        value={selecionadas}
                        onChange={(_, values) => {
                          setForm((prev) => ({
                            ...prev,
                            marcas: (Array.isArray(prev.marcas) ? prev.marcas : []).map((item) => {
                              const refItem = String(item?.refmarca || item?.ref || "").trim();

                              if (refItem !== refmarca) {
                                return item;
                              }

                              const existentes = Array.isArray(item?.submarcas)
                                ? item.submarcas
                                : [];

                              return {
                                ...item,
                                submarcas: values.map((option) => {
                                  const existente = existentes.find(
                                    (x) =>
                                      String(x?.refsubmarca || x?.ref || "") ===
                                      String(option?.ref || "")
                                  );

                                  return {
                                    ...(existente || {}),
                                    refsubmarca: option?.ref || "",
                                    submarca: option?.nome || option?.ref || "",
                                    sentido: modoSubmarcas.value,
                                  };
                                }),
                              };
                            }),
                          }));
                        }}
                        isOptionEqualToValue={(option, value) =>
                          String(option?.ref) === String(value?.ref)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={
                              modoSubmarcas.value === ""
                                ? "Todas"
                                : selecionadas.length === 0
                                ? "Selecione..."
                                : ""
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingSubmarcas[refmarca] ? (
                                    <CircularProgress size={18} />
                                  ) : null}

                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </MDBox>

                    <MDBox>
                      <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                        Modo das embalagens
                      </MDTypography>

                      <Autocomplete
                        size="small"
                        options={MODOS_EMBALAGENS}
                        getOptionLabel={(option) => option?.label || ""}
                        value={modoEmbalagens}
                        onChange={(_, value) => {
                          const novoSentido = value?.value || "";

                          setForm((prev) => ({
                            ...prev,
                            marcas: (Array.isArray(prev.marcas) ? prev.marcas : []).map((item) => {
                              const refItem = String(item?.refmarca || item?.ref || "").trim();

                              if (refItem !== refmarca) {
                                return item;
                              }

                              return {
                                ...item,
                                embalagens_sentido: novoSentido,
                                embalagens: novoSentido === "" ? [] : item?.embalagens || [],
                              };
                            }),
                          }));
                        }}
                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                        disableClearable
                        renderInput={(params) => <TextField {...params} />}
                      />
                    </MDBox>

                    <MDBox>
                      <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                        Embalagens
                      </MDTypography>

                      <Autocomplete
                        multiple
                        size="small"
                        disableCloseOnSelect
                        options={opcoesEmbalagens}
                        loading={Boolean(loadingEmbalagens[refmarca])}
                        disabled={modoEmbalagens.value === ""}
                        filterOptions={filtroEmbalagens}
                        getOptionLabel={(option) => option?.nome || option?.ref || ""}
                        value={embalagensSelecionadas}
                        onChange={(_, values) => {
                          setForm((prev) => ({
                            ...prev,
                            marcas: (Array.isArray(prev.marcas) ? prev.marcas : []).map((item) => {
                              const refItem = String(item?.refmarca || item?.ref || "").trim();

                              if (refItem !== refmarca) {
                                return item;
                              }

                              const existentes = Array.isArray(item?.embalagens)
                                ? item.embalagens
                                : [];

                              return {
                                ...item,
                                embalagens: values.map((option) => {
                                  const existente = existentes.find(
                                    (x) =>
                                      String(x?.refembalagem || x?.ref || "") ===
                                      String(option?.ref || "")
                                  );

                                  return {
                                    ...(existente || {}),
                                    refembalagem: option?.ref || "",
                                    embalagem: option?.nome || option?.ref || "",
                                    sentido: modoEmbalagens.value,
                                  };
                                }),
                              };
                            }),
                          }));
                        }}
                        isOptionEqualToValue={(option, value) =>
                          String(option?.ref) === String(value?.ref)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={
                              modoEmbalagens.value === ""
                                ? "Todas"
                                : embalagensSelecionadas.length === 0
                                ? "Selecione..."
                                : ""
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingEmbalagens[refmarca] ? (
                                    <CircularProgress size={18} />
                                  ) : null}

                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </MDBox>

                    <MDBox>
                      <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                        Modo dos materiais
                      </MDTypography>

                      <Autocomplete
                        size="small"
                        options={MODOS_MATERIAIS}
                        getOptionLabel={(option) => option?.label || ""}
                        value={modoMateriais}
                        onChange={(_, value) => {
                          const novoSentido = value?.value || "";

                          setForm((prev) => ({
                            ...prev,
                            marcas: (Array.isArray(prev.marcas) ? prev.marcas : []).map((item) => {
                              const refItem = String(item?.refmarca || item?.ref || "").trim();

                              if (refItem !== refmarca) {
                                return item;
                              }

                              return {
                                ...item,
                                materiais_sentido: novoSentido,
                                materiais: novoSentido === "" ? [] : item?.materiais || [],
                              };
                            }),
                          }));
                        }}
                        isOptionEqualToValue={(option, value) => option?.value === value?.value}
                        disableClearable
                        renderInput={(params) => <TextField {...params} />}
                      />
                    </MDBox>

                    <MDBox>
                      <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                        Materiais
                      </MDTypography>

                      <Autocomplete
                        multiple
                        size="small"
                        disableCloseOnSelect
                        options={opcoesMateriais}
                        ListboxProps={{
                          sx: {
                            maxHeight: "35vh",
                            overflowY: "auto",
                          },
                        }}
                        inputValue={pesquisaMateriais[refmarca] || ""}
                        onInputChange={(_, novoValor, reason) => {
                          if (reason === "input") {
                            setPesquisaMateriais((prev) => ({
                              ...prev,
                              [refmarca]: novoValor,
                            }));
                          }

                          if (reason === "clear") {
                            setPesquisaMateriais((prev) => ({
                              ...prev,
                              [refmarca]: "",
                            }));
                          }
                        }}
                        onBlur={() => {
                          setPesquisaMateriais((prev) => ({
                            ...prev,
                            [refmarca]: "",
                          }));
                        }}
                        loading={Boolean(loadingMateriais[refmarca])}
                        disabled={modoMateriais.value === ""}
                        filterOptions={filtroMateriais}
                        getOptionLabel={(option) => {
                          const ref = String(option?.ref || "").trim();
                          const nome = String(option?.nome || "").trim();

                          if (ref && nome) {
                            return `${ref} — ${nome}`;
                          }

                          return nome || ref;
                        }}
                        renderOption={(props, option) => {
                          const ref = String(option?.ref || option?.refmaterial || "").trim();
                          const nome = String(option?.nome || option?.material || "").trim();
                          const url = imgsMateriaisByRef?.[ref] || "";

                          return (
                            <li {...props}>
                              <MDBox display="flex" alignItems="center" gap={1} width="100%">
                                {url ? (
                                  <img
                                    src={url}
                                    alt={ref}
                                    style={{
                                      width: 26,
                                      height: 26,
                                      objectFit: "contain",
                                      borderRadius: 6,
                                      border: "1px solid #eee",
                                      background: "#fff",
                                      flexShrink: 0,
                                    }}
                                  />
                                ) : (
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: 26,
                                      height: 26,
                                      flexShrink: 0,
                                    }}
                                  />
                                )}

                                <span>{ref && nome ? `${ref} — ${nome}` : nome || ref}</span>
                              </MDBox>
                            </li>
                          );
                        }}
                        value={materiaisSelecionados}
                        onChange={(_, values) => {
                          setForm((prev) => ({
                            ...prev,
                            marcas: (Array.isArray(prev.marcas) ? prev.marcas : []).map((item) => {
                              const refItem = String(item?.refmarca || item?.ref || "").trim();

                              if (refItem !== refmarca) {
                                return item;
                              }

                              const existentes = Array.isArray(item?.materiais)
                                ? item.materiais
                                : [];

                              return {
                                ...item,
                                materiais: values.map((option) => {
                                  const existente = existentes.find(
                                    (x) =>
                                      String(x?.refmaterial || x?.ref || "") ===
                                      String(option?.ref || "")
                                  );

                                  return {
                                    ...(existente || {}),
                                    refmaterial: option?.ref || "",
                                    material: option?.nome || option?.ref || "",
                                    refsubmarca: option?.refsubmarca || "",
                                    submarca: option?.submarca || "",
                                    refembalagem: option?.refembalagem || "",
                                    embalagem: option?.embalagem || "",
                                    sentido: modoMateriais.value,
                                  };
                                }),
                              };
                            }),
                          }));
                        }}
                        isOptionEqualToValue={(option, value) =>
                          String(option?.ref) === String(value?.ref)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={
                              modoMateriais.value === ""
                                ? "Todos"
                                : materiaisSelecionados.length === 0
                                ? "Selecione..."
                                : ""
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingMateriais[refmarca] ? (
                                    <CircularProgress size={18} />
                                  ) : null}

                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </MDBox>
                  </MDBox>
                </MDBox>
              );
            })}

            <MDBox
              sx={{
                gridColumn: {
                  xs: "auto",
                  sm: "1 / -1",
                },
              }}
            >
              <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                Modo das marcas
              </MDTypography>

              <Autocomplete
                size="small"
                options={MODOS_MARCAS}
                getOptionLabel={(option) => option?.label || ""}
                value={modoMarcasSelecionado}
                onChange={(_, value) =>
                  setForm((prev) => ({
                    ...prev,
                    marcas_modo: value?.value || "QUALQUER",
                  }))
                }
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                disableClearable
                renderInput={(params) => <TextField {...params} placeholder="Selecione..." />}
              />
            </MDBox>

            <MDBox
              sx={{
                gridColumn: {
                  xs: "auto",
                  sm: "1 / -1",
                },
              }}
            >
              <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                Valor da empresa
              </MDTypography>

              <MDInput
                type="number"
                fullWidth
                value={form.valor_empresa}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    valor_empresa: e.target.value,
                  }))
                }
                inputProps={{
                  min: 0,
                  step: "0.001",
                }}
              />
            </MDBox>
          </MDBox>
        </MDBox>
      </DialogContent>

      <DialogActions>
        <MDButton color="secondary" onClick={onClose}>
          Cancelar
        </MDButton>

        <MDButton
          color="info"
          onClick={guardar}
          disabled={
            !String(form.nome || "").trim() ||
            !String(form.reffabricante || "").trim() ||
            Number(form.ordem || 0) <= 0
          }
        >
          Guardar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
